/* eslint-disable react/display-name */
import React from 'react';

import { Container, Sidebar, SidebarMenu, Button } from 'ui/components/styled';
import { Logo, CompanySelect, LoadingOverlay, Navbar } from 'ui/components/shared';
import { useUser, useLayout, useToasts, useSlowUpdate } from 'hooks';
import useMediaQuery from 'hooks/useMediaQuery';
import device from 'ui/theme/device';
import { Badge } from 'ui/components/shared/Badge';
import Hamburger from 'hamburger-react';
import Text from 'ui/components/styled/Text';
import { version } from '../../../../../package.json';
import ReactTooltip from 'react-tooltip';

const { REACT_APP_CURRENT_DATE = '' } = process.env;

export const UserLayout = React.forwardRef(({ children, ...props }, ref) => {
  const { isLoggedIn, isAdmin } = useUser();
  const { isLoading, setLayoutLoading } = useLayout();
  const { removeAllToasts } = useToasts();
  const loading = useSlowUpdate(isLoading, () => isLoading === false, 300);
  const contentRef = ref || React.useRef();
  const isLarge = useMediaQuery(device.large);
  const [toggleHamburger, setToggleHamburger] = React.useState(false);

  React.useEffect(() => {
    return () => {
      removeAllToasts();
      setLayoutLoading(false);
    };
  }, []);

  const isSidebarOpen = toggleHamburger || isLarge;

  return (
    isLoggedIn && (
      <Container flexDirection="row">
        {isSidebarOpen && (
          <Sidebar>
            <Logo variant="white" mx="l" mt="xl" mb="s" />
            <Container justifyContent="space-between">
              <Container height="auto">
                <CompanySelect setToggleHamburger={setToggleHamburger} isLarge={isLarge} />
                <SidebarMenu
                  links={
                    isAdmin
                      ? [
                          { label: 'Dashboard', to: '/admin/dashboard' },
                          { label: 'Orphans', to: '/orphans' },
                          { label: 'Companies', to: '/admin/companies' },
                          { label: 'User settings', to: '/settings' },
                          // { label: 'Statistics', to: '/admin/statistics', disabled: true },
                          { label: 'User management', to: '/admin/users' },
                          { label: 'Reports', to: '/admin/reports' },
                          { label: 'Logs', to: '/admin/logs' },
                          { label: 'Notifications', to: '/admin/notifications' },
                          { label: 'Branding', to: '/admin/branding' },
                        ]
                      : [
                          { label: 'Dashboard', to: '/dashboard' },
                          { label: 'Orphans', to: '/orphans' },
                          { label: 'Companies', to: '/companies' },
                          { label: 'User settings', to: '/settings' },
                          { label: 'Feedback', to: '/feedback' },
                        ]
                  }
                />
              </Container>
              <Container height="auto" px="l" py="l">
                {!isLarge && (
                  <Container height="auto" alignItems="center">
                    <Badge listDirection="top" isSidebar={true} />
                  </Container>
                )}

                <Button
                  variant="outline"
                  look="flat"
                  width="auto"
                  mx="auto"
                  data-tip
                  data-for="About"
                >
                  <Container
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap="5px"
                    flexDirection="row"
                  >
                    {'About '} <img src="/assets/img/icons/info.svg" alt="info" width="12px" />
                    {/* <DropDown
                      title="About"
                      icon={<img src="/assets/img/icons/info.svg" alt="info" width="12px" />}
                    >
                      <Container textAlign="center">
                        <Container maxWidth="90%" alignItems="center">
                          <Title variant="dark" mb="xs">
                            About
                          </Title>
                          <Text>
                            <strong>
                              Version: {version}
                              {REACT_APP_CURRENT_DATE && (
                                <span> released on {REACT_APP_CURRENT_DATE}</span>
                              )}
                            </strong>
                          </Text>
                          <Text lineHeight="1.7">
                            © Copyright 2022, Process Watch Technologies, LLC. All Rights Reserved.
                          </Text>
                        </Container>
                      </Container>
                    </DropDown> */}
                  </Container>
                </Button>
                <ReactTooltip
                  id="About"
                  type="light"
                  place="top"
                  effect="solid"
                  clickable
                  event="click"
                  globalEventOff="click"
                >
                  <Container maxWidth="200px" textAlign="center" zIndex={310000}>
                    <Container>
                      <Text fontSize="16px" mb="xs">
                        <strong>Version: {version}</strong>
                        {REACT_APP_CURRENT_DATE && (
                          <Text mt="xs">
                            <strong>Released on: {REACT_APP_CURRENT_DATE}</strong>
                          </Text>
                        )}
                      </Text>
                      <Text lineHeight="1.5">
                        © Copyright 2022, Process Watch Technologies, LLC. All Rights Reserved.
                      </Text>
                    </Container>
                  </Container>
                </ReactTooltip>
              </Container>
            </Container>
          </Sidebar>
        )}

        <Container
          ref={contentRef}
          width="auto"
          overflow="auto"
          flexGrow="1"
          {...props}
          flexDirection="column"
        >
          {loading && <LoadingOverlay scrollable={contentRef}></LoadingOverlay>}
          {isLarge ? (
            <Navbar />
          ) : (
            <Container flexDirection="row" justifyContent="flex-end" height="auto">
              <Hamburger toggled={toggleHamburger} toggle={setToggleHamburger} />
            </Container>
          )}
          {children}
        </Container>
      </Container>
    )
  );
});

export const withUserLayout =
  (Component) =>
  (...props) =>
    (
      <UserLayout>
        <Component {...props} />
      </UserLayout>
    );
